import { call, cancelled, put, race, select, take } from 'redux-saga/effects';

import { GeneratorResult, OperationStatus, selectTyped, takeIf } from '@pressreader/appstore';
import { selectUser } from '@pressreader/authentication-core';
import { selectBookmarksEnabled } from '@pressreader/global-config';
import { error } from '@pressreader/logger';
import { loadUserCollectionsAction, loadUserCollectionsFinishedActionList } from '@pressreader/social';
import { getArticleBookmarkCollections } from '@pressreader/social-api';
import type { ArticleCollections } from '@pressreader/social-types';
import type { ExtractReturnType } from '@pressreader/types';

import {
    loadBookmarksForArticleBegan,
    loadBookmarksForArticleCancelled,
    loadBookmarksForArticleDone,
    loadBookmarksForArticleFailed,
} from '../actions';
import { selectArticlesBookmarks } from '../selectors';

function* getOrWaitForArticleBookmarks(articleId: string): GeneratorResult<ArticleCollections[] | null> {
    const bookmarks: ReturnType<typeof selectArticlesBookmarks> = yield select(selectArticlesBookmarks);
    const articleBookmarksMeta = bookmarks[articleId];

    if (articleBookmarksMeta && articleBookmarksMeta.status !== OperationStatus.None) {
        if (articleBookmarksMeta.status === OperationStatus.Done) {
            return articleBookmarksMeta.bookmarks;
        }

        if (articleBookmarksMeta.status === OperationStatus.Failed) {
            return null;
        }

        yield race({
            done: takeIf(loadBookmarksForArticleDone, a => a.payload.articleId === articleId),
            failed: takeIf(loadBookmarksForArticleFailed, a => a.payload.articleId === articleId),
            cancelled: takeIf(loadBookmarksForArticleCancelled, a => a.payload.articleId === articleId),
        });

        return yield* getOrWaitForArticleBookmarks(articleId);
    }

    return null;
}

function* loadArticleBookmarks(articleId: string): GeneratorResult<ArticleCollections[]> {
    const enabled = yield* selectTyped(selectBookmarksEnabled);
    if (!enabled) {
        return [];
    }

    let bookmarks = yield* getOrWaitForArticleBookmarks(articleId);

    if (bookmarks) {
        return bookmarks;
    }

    const user: ReturnType<typeof selectUser> = yield select(selectUser);
    if (!user.isAuthenticated) {
        return [];
    }

    try {
        yield put(loadBookmarksForArticleBegan({ articleId }));

        yield put(loadUserCollectionsAction({ isForce: false }));
        yield take([...loadUserCollectionsFinishedActionList]);

        const bookmark: ExtractReturnType<typeof getArticleBookmarkCollections> = yield call(getArticleBookmarkCollections, articleId);
        if (!bookmark.collections?.length) {
            bookmarks = [];
        } else {
            bookmarks = [bookmark];
        }

        yield put(loadBookmarksForArticleDone({ articleId, bookmarks }));

        return bookmarks;
    } catch (e) {
        error(e);
        yield put(loadBookmarksForArticleFailed({ articleId, error: e }));
    } finally {
        // Cancellation unwinds through `finally`, never `catch`: release the `Pending` this load marked so
        // later waiters are not blocked for the page's life. The `try` opens with the Began put, and a put
        // is never cancelled — once yielded it lands even if the task is cancelled while it is still queued —
        // so every cancellation that reaches here follows a Began and must be answered.
        const isCancelled: boolean = yield cancelled();
        if (isCancelled) {
            yield put(loadBookmarksForArticleCancelled({ articleId }));
        }
    }

    return [];
}

export { loadArticleBookmarks };
